import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSuppliers } from '../../services/supplierService';
import ExpandableSupplierGrid from '../../components/suppliers/ExpandableSupplierGrid';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar
} from '@mui/material';

const SupplierList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Check for notifications from location state
  useEffect(() => {
    if (location.state?.notification) {
      setSnackbar({
        open: true,
        message: location.state.notification.message,
        severity: location.state.notification.severity || 'info'
      });

      // Clear the location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // State for search filter with debouncing
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState('');

  // Function to handle search filter changes
  const handleSearchChange = (searchText) => {
    setSearchInputValue(searchText);
  };

  // Debounce the search filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchFilter(searchInputValue);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInputValue]);

  // Fetch suppliers when debounced search filter changes
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoading(true);
        // Pass search filter to the API
        const data = await getSuppliers({
          search: debouncedSearchFilter
        });
        setSuppliers(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        setError('Failed to load suppliers. Please try again.');
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [debouncedSearchFilter]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Supplier Management
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      ) : (
        <ExpandableSupplierGrid
          suppliers={suppliers}
          onSearchChange={handleSearchChange}
          initialSearchValue={searchInputValue}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupplierList;
